import React, { useCallback, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash } from 'react-icons/fa';
import CommonTable from 'components/commonTable/CommonTable';
import CommonPagination from 'components/commonPagination/CommonPagination';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import ConfirmModal from 'components/popUpModel/DeleteModal';
import useListState from 'hooks/useListState';
import { sideBarRoutes } from 'utils/StaticData';
import {
  DeletePayoutSetting,
  GetPayoutSettingList,
  TogglePayoutSetting,
} from '../../../reactRedux/thunks/payoutSettingThunk/payoutSettingThunk';
import moment from 'moment';
import { useRef } from 'react';

const PayoutSettingList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tabletextColor } = useColorModeVariables();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const toggleRef = useRef({ id: null, isActive: null });
  const deleteRef = useRef(null);

  const { params, setParams, page, setPage } = useListState({
    defaults: { limit: 10 },
  });

  const { isLoading, payoutSettingList } = useSelector(
    (state) => state.PayoutSettingSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);

  const isAdmin = adminDetails?.role === sideBarRoutes?.Admin;
  const canView =
    adminDetails?.role === sideBarRoutes?.Admin ||
    adminDetails?.role === sideBarRoutes?.Dealer;

  const getList = useCallback(() => {
    dispatch(GetPayoutSettingList());
  }, [dispatch]);

  useEffect(() => {
    if (canView) {
      getList();
    }
  }, [getList, canView]);

  const openToggleModal = (id, currentActive) => {
    toggleRef.current = { id, isActive: !currentActive };
    onOpen();
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row) => row?.settingId, {
      id: 'settingId',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Setting ID
        </Text>
      ),
      cell: (info) => {
        const sid = info.getValue();
        const display = sid ? sid.slice(0, 8) : 'N/A';
        return (
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            title={sid || ''}
          >
            {display}
          </Text>
        );
      },
    }),

    columnHelper.accessor((row) => row?.platformAccountName, {
      id: 'platformAccountName',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Platform Account
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.connectedAccountName, {
      id: 'connectedAccountName',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Connected Account
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.revenueSplitRatio, {
      id: 'revenueSplitRatio',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Split Ratio (%)
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" align="left">
          {info.getValue() !== null && info.getValue() !== undefined
            ? `${info.getValue()}%`
            : 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.payoutFrequency, {
      id: 'payoutFrequency',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Frequency
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          align="left"
          textTransform="capitalize"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor(
      (row) => ({
        frequency: row?.payoutFrequency,
        dayOfWeek: row?.scheduledDayOfWeek,
        dayOfMonth: row?.scheduledDayOfMonth,
        time: row?.scheduledTime,
      }),
      {
        id: 'schedule',
        header: () => (
          <Text
            whiteSpace="nowrap"
            align="left"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
            fontFamily="Montserrat"
          >
            Schedule
          </Text>
        ),
        cell: (info) => {
          const { frequency, dayOfWeek, dayOfMonth, time } = info.getValue();
          const DAY_NAMES = [
            '',
            'Mon',
            'Tue',
            'Wed',
            'Thu',
            'Fri',
            'Sat',
            'Sun',
          ];
          let label = 'N/A';
          if (frequency === 'weekly' && dayOfWeek && time) {
            label = `${DAY_NAMES[dayOfWeek] || dayOfWeek} ${time}`;
          } else if (frequency === 'monthly' && dayOfMonth && time) {
            label = `${dayOfMonth}th ${time}`;
          }
          return (
            <Text
              color={tabletextColor}
              fontSize="sm"
              align="left"
              whiteSpace="nowrap"
            >
              {label}
            </Text>
          );
        },
      },
    ),

    columnHelper.accessor((row) => row?.next3SettlementDates, {
      id: 'nextSettlement',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Next Settlement
        </Text>
      ),
      cell: (info) => {
        const dates = info.getValue();
        const next = Array.isArray(dates) && dates[0] ? dates[0] : null;
        return (
          <Text
            color={tabletextColor}
            fontSize="sm"
            align="left"
            whiteSpace="nowrap"
          >
            {next
              ? moment(next).utc().format('DD MMM YYYY HH:mm [UTC]')
              : 'N/A'}
          </Text>
        );
      },
    }),

    columnHelper.accessor((row) => row?.isActive, {
      id: 'isActive',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Status
        </Text>
      ),
      cell: (info) => (
        <Badge
          colorScheme={info.getValue() ? 'green' : 'gray'}
          variant="solid"
          fontSize="xs"
        >
          {info.getValue() ? 'Active' : 'Inactive'}
        </Badge>
      ),
    }),

    columnHelper.accessor((row) => row, {
      id: 'actions',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Actions
        </Text>
      ),
      cell: (info) => {
        const row = info.getValue();
        return (
          <Flex className="no-navigate" align="center" gap="12px">
            <Switch
              colorScheme="green"
              isChecked={!!row?.isActive}
              isDisabled={!isAdmin}
              onChange={() => openToggleModal(row?.id, row?.isActive)}
            />
            {isAdmin && (
              <Icon
                as={FaTrash}
                w="14px"
                h="14px"
                color="red.500"
                cursor="pointer"
                onClick={() => {
                  deleteRef.current = row?.id;
                  onDeleteOpen();
                }}
              />
            )}
          </Flex>
        );
      },
    }),
  ];

  const listData = Array.isArray(payoutSettingList) ? payoutSettingList : [];
  const paginatedData = listData.slice(
    (page - 1) * params.limit,
    page * params.limit,
  );

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      {isAdmin && (
        <Flex justify="flex-end" mb={4}>
          <Button
            bg="reviveOrange"
            color="primary"
            borderRadius="12px"
            _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
            onClick={() => navigate('/admin/payout-settings/create')}
          >
            <Icon
              as={FaPlus}
              width="15px"
              height="15px"
              marginRight="5px"
              color="inherit"
            />
            Add Payout Setting
          </Button>
        </Flex>
      )}

      <CommonTable
        columns={columns}
        data={paginatedData}
        isLoading={isLoading}
        keyValue="id"
        rowclick={['actions']}
        isLink={true}
        parentLink="/admin/payout-settings"
      />

      {listData.length > params.limit && (
        <CommonPagination
          totalCount={listData.length}
          activePage={page}
          pageCount={Math.ceil(listData.length / params.limit)}
          onPageChange={setPage}
          itemsPerPage={params.limit}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      )}

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Are you sure you want to ${toggleRef.current.isActive ? 'enable' : 'disable'} auto payout for this setting?`}
        SolidBTNText="Confirm"
        onDelete={() => {
          dispatch(
            TogglePayoutSetting(
              toggleRef.current.id,
              toggleRef.current.isActive,
              () => {
                onClose();
              },
            ),
          );
        }}
        loader={isLoading}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        title="Are you sure you want to delete this payout setting? This action cannot be undone."
        SolidBTNText="Delete"
        onDelete={() => {
          dispatch(
            DeletePayoutSetting(deleteRef.current, () => {
              onDeleteClose();
            }),
          );
        }}
        loader={isLoading}
      />
    </Box>
  );
};

export default PayoutSettingList;
